import { useEffect, useState, useRef, Component, useCallback } from 'react';
import { EditorState, DefaultDraftBlockRenderMap, Modifier, AtomicBlockUtils, ContentBlock } from 'draft-js';
import { Map } from 'immutable';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import DOMPurify from 'dompurify';
import { convertFromHTML } from 'draft-convert';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

interface TextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

// 插入youtube embed
const YOUTUBE_REGEX = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/;

function insertYoutubeEmbed(editorState: EditorState): EditorState {
  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const blockKey = selection.getStartKey();
  const block = contentState.getBlockForKey(blockKey);
  const text = block.getText();
  const match = text.match(YOUTUBE_REGEX);

  if (!match) return editorState;

  const videoId = match[1];
  const embedUrl = `https://www.youtube.com/embed/${videoId}`;

  // 找到 YouTube URL 的開始和結束位置
  const start = text.indexOf(match[0]);
  const end = start + match[0].length;

  // 創建新的 selection 只包含 YouTube URL
  const newSelection = selection.merge({
    anchorOffset: start,
    focusOffset: end,
  });

  // 創建 iframe entity
  const contentStateWithEntity = contentState.createEntity(
    'IFRAME',
    'IMMUTABLE',
    { src: embedUrl }
  );

  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  // 先移除 URL 文字
  const contentStateWithoutLink = Modifier.replaceText(
    contentStateWithEntity,
    newSelection,
    ''
  );

  // 插入 atomic block
  const contentStateWithBlock = AtomicBlockUtils.insertAtomicBlock(
    EditorState.push(editorState, contentStateWithoutLink, 'remove-range'),
    entityKey,
    ' '
  );

  return EditorState.forceSelection(
    contentStateWithBlock,
    contentStateWithBlock.getSelection()
  );
}

class IframeComponent extends Component<any> {
  render() {
    const { block, contentState } = this.props;
    const entity = contentState.getEntity(block.getEntityAt(0));
    const { src } = entity.getData();

    return (
      <div className="iframe-wrapper">
        <iframe
          src={src}
          title={`iframe-${src}`}
          frameBorder="0"
          allowFullScreen
          style={{ width: '100%', height: '400px' }}
        />
      </div>
    );
  }
}

const blockRenderMap = Map({
  'atomic': {
    element: 'figure',
    wrapper: <div className="atomic-block-wrapper" />,
  }
}).merge(DefaultDraftBlockRenderMap);

const TextEditor = ({ value, onChange }: TextEditorProps) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [showClearButton, setShowClearButton] = useState(true);
  const [isProcessingYoutube, setIsProcessingYoutube] = useState(false);
  const blurTimeoutRef = useRef<number | null>(null);

  const createEditorState = (html: string) => {
    try {
      const cleanHtml = DOMPurify.sanitize(html, {
        ADD_TAGS: ['iframe', 'figure'],
        ADD_ATTR: [
          'allowfullscreen',
          'frameborder',
          'src',
          'style',
          'class',
          'width',
          'height',
        ],
      });

      const processedHtml = cleanHtml.replace(
        /<figure class="atomic-block"><figure class="atomic-block">(.*?)<\/figure><\/figure>/g,
        '<figure class="atomic-block">$1</figure>'
      );

      const contentState = convertFromHTML({
        htmlToEntity: (
          nodeName: string,
          node: HTMLElement,
          createEntity: (
            type: string,
            mutability: string,
            data?: Record<string, any>
          ) => string
        ): string | undefined => {
          if (nodeName === 'iframe') {
            return createEntity('IFRAME', 'IMMUTABLE', {
              src: node.getAttribute('src'),
            });
          }
          if (nodeName === 'img') {
            return createEntity('IMAGE', 'IMMUTABLE', {
              src: node.getAttribute('src'),
              alt: node.getAttribute('alt') || '',
              width: node.getAttribute('width'),
              height: node.getAttribute('height'),
            });
          }
          return undefined;
        },
      
        htmlToBlock: (
          nodeName: string,
          node: HTMLElement
        ): string | undefined => {
          if (
            nodeName === 'iframe' ||
            nodeName === 'img' ||
            (nodeName === 'figure' &&
              node.classList?.contains('atomic-block'))
          ) {
            return 'atomic';
          }
          return undefined;
        },
      })(processedHtml);

      if (!contentState) {
        return EditorState.createEmpty();
      }

      return EditorState.createWithContent(contentState);
    } catch (error) {
      console.error('Error creating editor state:', error);
      return EditorState.createEmpty();
    }
  };

  const handleBlur = () => {
    try {
      const contentState = editorState.getCurrentContent();
      const options = {
        blockRenderers: {
          atomic: (block: ContentBlock): string => {
            const entityKey = block.getEntityAt(0);
            if (!entityKey) return '';

            const entity = contentState.getEntity(entityKey);
            const type = entity.getType();
            const { src } = entity.getData();

            if (type === 'IFRAME') {
              return `<figure class="atomic-block"><iframe src="${src}" frameborder="0" allowfullscreen style="width: 100%; height: 400px;"></iframe></figure>`;
            } else if (type === 'IMAGE') {
              return `<figure class="atomic-block"><img src="${src}" style="max-width: 825px; width: 100%" /></figure>`;
            }

            return '';
          }
        },
        entityStyleFn: (entity: any) => {
          const type = entity.getType().toLowerCase();
          const data = entity.getData();

          if (type === 'iframe') {
            return {
              element: 'iframe',
              attributes: {
                src: data.src,
                frameBorder: '0',
                allowFullScreen: true,
                style: 'width: 100%; height: 400px;',
              },
            };
          }

          if (type === 'image') {
            return {
              element: 'img',
              attributes: {
                src: data.src,
                style: 'max-width: 825px; width: 100%;',
              },
            };
          }

          return undefined;
        }
      };

      let htmlContent = stateToHTML(contentState, options);

      // 清理和處理 HTML
      htmlContent = DOMPurify.sanitize(htmlContent, {
        ADD_TAGS: ['iframe', 'figure'],
        ADD_ATTR: ['allowfullscreen', 'frameborder', 'src', 'style', 'class'],
      });

      // 確保 iframe 被正確包裝
      if (htmlContent.includes('<iframe')) {
        htmlContent = htmlContent.replace(
          /<iframe([^>]*)><\/iframe>/g,
          '<figure class="atomic-block"><iframe$1></iframe></figure>'
        );
      }

      console.log(htmlContent);
      console.log(typeof htmlContent);
      onChange(htmlContent);
    } catch (error) {
      console.error('Error in handleBlur:', error);
    }
  };

  const handleEditorChange = (newEditorState: EditorState) => {
    if (isProcessingYoutube) {
      setEditorState(newEditorState);
      return;
    }

    const contentState = newEditorState.getCurrentContent();
    const selection = newEditorState.getSelection();
    const blockKey = selection.getStartKey();
    const block = contentState.getBlockForKey(blockKey);
    const text = block.getText();

    const match = text.match(YOUTUBE_REGEX);
    if (match) {
      setIsProcessingYoutube(true);
      const processedState = insertYoutubeEmbed(newEditorState);
      setEditorState(processedState);
      setIsProcessingYoutube(false);
    } else {
      setEditorState(newEditorState);
    }
  };

  const handleBlurDelayed = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    blurTimeoutRef.current = window.setTimeout(() => {
      setShowClearButton(true);
      handleBlur();
    }, 150);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setShowClearButton(false);
  };

  const handleClear = () => {
    handleEditorChange(EditorState.createEmpty());
    onChange('');
  };

  const uploadImageCallback = (file: File): Promise<{ data: { link: string } }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const imageUrl = reader.result as string;
        resolve({ data: { link: imageUrl } });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const createBlockRendererFn = (editorState: EditorState) => (block: ContentBlock) => {
    if (block.getType() === 'atomic') {
      const contentState = editorState.getCurrentContent();
      const entityKey = block.getEntityAt(0);
      if (!entityKey) return null;
  
      const entity = contentState.getEntity(entityKey);
      const type = entity.getType();
  
      if (type === 'IFRAME') {
        return {
          component: IframeComponent,
          editable: false,
          props: {
            contentState,
          },
        };
      }
  
      if (type === 'IMAGE') {
        return {
          component: ImageComponent,
          editable: false,
          props: {
            contentState,
          },
        };
      }
    }
    return null;
  };

  const ImageComponent = (props: any) => {
    const { block, contentState } = props;
    const entity = contentState.getEntity(block.getEntityAt(0));
    const { src, alt } = entity.getData();
  
    return (
      <div>
        <img
          src={src}
          alt={alt || ''}
          style={{
            width: '100%',
            maxWidth: '825px',
            height: 'auto',
          }}
        />
      </div>
    );
  };

  useEffect(() => {
    if (!value) {
      setEditorState(EditorState.createEmpty());
      return;
    }
    // 避免無效更新
    const currentHtml = stateToHTML(editorState.getCurrentContent());
    if (value && currentHtml === value) return;

    const newEditorState = createEditorState(value);
    console.log('newEditorState', newEditorState);
    setEditorState(newEditorState);
  }, [value]);

  return (
    <div style={{ border: '1px solid #ccc', borderRadius: '5px', position: 'relative' }}>
      {showClearButton && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: 'absolute',
            top: 38,
            right: 18,
            zIndex: 10,
            padding: '4px 8px',
            background: 'red',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Reset
        </button>
      )}
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
        onBlur={handleBlurDelayed}
        onFocus={handleFocus}
        blockRendererFn={createBlockRendererFn(editorState)}
        blockRenderMap={blockRenderMap}
        toolbar={{
          options: [
            'inline',
            'blockType',
            'fontSize',
            'fontFamily',
            'list',
            'textAlign',
            'colorPicker',
            'link',
            'image',
            'remove',
            'history'
          ],
          inline: {
            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
            bold: { className: 'bordered-option-classname' },
            italic: { className: 'bordered-option-classname' },
            underline: { className: 'bordered-option-classname' },
            strikethrough: { className: 'bordered-option-classname' },
            code: { className: 'bordered-option-classname' }
          },
          blockType: {
            className: 'bordered-option-classname',
          },
          fontSize: {
            className: 'bordered-option-classname',
          },
          fontFamily: {
            className: 'bordered-option-classname',
          },
          list: {
            unordered: { className: 'bordered-option-classname' },
            ordered: { className: 'bordered-option-classname' },
            indent: { className: 'bordered-option-classname' },
            outdent: { className: 'bordered-option-classname' }
          },
          textAlign: {
            left: { className: 'bordered-option-classname' },
            center: { className: 'bordered-option-classname' },
            right: { className: 'bordered-option-classname' },
            justify: { className: 'bordered-option-classname' }
          },
          colorPicker: {
            className: 'bordered-option-classname',
          },
          link: {
            options: ['link', 'unlink'],
            defaultTargetOption: '_blank',
            className: 'bordered-option-classname',
          },
          image: {
            uploadCallback: uploadImageCallback,
            alt: { present: true, mandatory: false },
            defaultSize: {
              height: 'auto',
              width: '100%'
            },
            className: 'bordered-option-classname',
            previewImage: true,
            inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
            alignmentEnabled: true,
            defaultAlignment: 'center',
            addParagraphBefore: true,
            addParagraphAfter: true
          },
          remove: { className: 'bordered-option-classname' },
          history: {
            undo: { className: 'bordered-option-classname' },
            redo: { className: 'bordered-option-classname' }
          }
        }}
        editorStyle={{
          minHeight: '200px',
          padding: '10px'
        }}
        toolbarClassName="editor-toolbar"
        wrapperClassName="editor-wrapper"
        editorClassName="editor-content"
      />
    </div>
  );
};

export default TextEditor;